import {
  readUsConsent,
  resetConsentCache,
  type ConsentBasis,
} from '@redbrickmedia/duplex.user-identity'
import { isDebugEnabled } from '../debug/isDebugEnabled'
import debug from '../console'

const logger = debug.withTag('utils:consent')

/**
 * How long the ad path waits for a verdict before rendering anyway.
 *
 * The package's own budget is the real ceiling; this only stops a CMP that
 * never answers from holding every slot on the page. Ads are not the primary
 * enforcement point — Prebid and LiveRamp read the CMP themselves and AY has
 * its own after-consent trigger — so a bounded wait is the right trade against
 * holding inventory indefinitely.
 */
export const AD_GATE_MAX_WAIT_MS = 2000

export type ConsentVerdict = {
  granted: boolean
  /** `'error'` is ours; every other value comes from the package. */
  basis: ConsentBasis | 'error'
}

let pending: Promise<ConsentVerdict> | null = null
let settled: ConsentVerdict | null = null

async function read(country?: string): Promise<ConsentVerdict> {
  // The package reports the basis through `onEvent` while returning only the
  // boolean, so capture it on the way past.
  let basis: ConsentBasis | 'error' = 'none'

  try {
    const granted = await readUsConsent({
      country,
      debug: isDebugEnabled(),
      onEvent: (event) => {
        if (event.type === 'resolved') {
          basis = event.basis
        }
      },
    })
    settled = { granted, basis }
  } catch (error) {
    // Documented never to reject, so this is a package regression rather than
    // an expected path. Deny: it is the only direction that cannot leak data.
    logger.warn('Consent read failed; denying', error)
    settled = { granted: false, basis: 'error' }
  }

  logger.debug(`Consent resolved: ${settled.granted} (basis: ${settled.basis})`)
  return settled
}

/**
 * Resolve consent for this pageview. Memoized — every caller shares one read,
 * and the verdict never changes once settled.
 *
 * `country` is only consulted if no CMP produces a signal at all, where an
 * EU/EEA/UK visitor fails closed rather than inheriting the US opt-out
 * default. Pass it on the first call; later calls reuse the same promise.
 */
export function resolveConsent(country?: string): Promise<ConsentVerdict> {
  pending ??= read(country)
  return pending
}

/**
 * Resolves once consent has settled, or once `maxWaitMs` has elapsed —
 * whichever comes first. For callers that want to *delay* work rather than
 * decide on it.
 *
 * Starts the read itself if nobody has. Returning early instead would fail
 * open silently the moment the `consent` plugin is renamed or reordered — no
 * error, no failing test, just ungated ads.
 */
export function whenConsentSettled(
  maxWaitMs: number = AD_GATE_MAX_WAIT_MS
): Promise<void> {
  if (settled !== null) return Promise.resolve()

  if (pending === null) {
    logger.warn('Consent read had not started; starting it now')
    void resolveConsent()
  }
  const inFlight = pending

  return new Promise<void>((resolve) => {
    const timer = globalThis.setTimeout(() => {
      logger.debug(`Consent still unsettled after ${maxWaitMs}ms; continuing`)
      resolve()
    }, maxWaitMs)

    void inFlight?.finally(() => {
      globalThis.clearTimeout(timer)
      resolve()
    })
  })
}

/** The settled verdict, or `null` while the read is still in flight. */
export function consentSnapshot(): ConsentVerdict | null {
  return settled
}

export function resetConsentGateForTests(): void {
  pending = null
  settled = null
  // The package memoizes its own read; clearing only ours would hand the next
  // test a stale verdict.
  resetConsentCache()
}
